'use client';
import cn from 'classnames';
import React, {
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from 'react';
import { useRouter } from 'next/navigation';
import { useScrollLock } from '@/components/hook/scroll';
import Image from 'next/image';

import { HEADER_SEARCH_FORM_ID } from '@/components/common/Header/Header';
import Text from '@/components/common/Text/Text';
import TabBar from '@/components/common/TabBar/TabBar';
import ProductCard from '@/components/common/ProductCard/ProductCard';
import PaginationBar from '@/components/common/PaginationBar/PaginationBar';

import { useSearchContext } from '@/components/hook/search';
import { useLoggedInUser } from '@/components/hook/user';
import { useFlags } from '@/components/hook/flags';
import { FLAG_KEYS } from '@/const/flags';
import { isPriveeMember } from '@/util/user_client';

import PinkFlower from 'public/img/logo/vivrelle-flower-pink.png';
import PriveeWordmark from 'public/img/logo/reserve-logo.svg';
import { ProductCardData } from '@/types/Product';

import PriveeHeroBanner from '@/components/common/PriveeHeroBanner/PriveeHeroBanner';
import PriveeInterestHero from '@/components/common/PriveeInterestHero/PriveeInterestHero';
import PLPSearchBar from '@/components/common/PLPSearchBar/PLPSearchBar';

import FilterMenu from './FilterMenu';
import {
  buildStateFromSearch,
  createFilterStateCopy,
  filterCountString,
  FILTER_QUERY_PARAM_KEYS,
  getQueryParamsFromFilterState,
  priveeInitialFilterState,
  runSearchWithFilterState
} from './dataTools';

import { PLPVariant } from './constants';

import './ProductListApp.style.scss';
import Button from '../Button/Button';

type ProductListAppProps = {
  className?: string;
  stateFromServer?: State;
  currentUrlPath: string;
  allFacets?: FacetState;
  variant?: PLPVariant;
  showSearch?: boolean;
  showFilters?: boolean;
  afterPagination?: ReactNode;
  onProductCardClick?: (e: React.MouseEvent) => void;
  /**
   * Hero/banner rendered above the filter bar. Generic injection slot used by
   * campaign PLPs (the Privée hero is still rendered internally by variant).
   */
  heroBanner?: ReactNode;
  /**
   * Base filter state that "Clear All"/Reset and the search bars revert to.
   * Campaign PLPs pass a state with the pinned occasion(s) so it survives
   * resets and searches. Falls back to the Privée/default base when omitted.
   */
  baseFilterState?: FilterState;
  /**
   * Facet values pinned and locked in the filter menu. Currently only
   * `occasion` is honored (campaign PLPs); other keys are intentionally
   * unsupported in this phase.
   */
  lockedFacets?: { occasion?: string[] };
};

export type FilterState = {
  searchQuery: string;
  collection: {
    collectionType: string[];
    types: string[];
  };
  subscriptionLevel: string[];
  brand: string[];
  color: string[];
  occasion: string[];
  material: string[];
  influencers: string[];
  inStock: boolean;
  newArrivals: boolean;
  wishlist: boolean;
  hitsCount: number;
  pageNo: number;
  priveeOnly?: boolean;
};

export type FacetState = {
  collection?: {
    [category: string]: string[];
  };
  brand?: {
    [displayValue: string]: number;
  };
  color?: {
    [displayValue: string]: number;
  };
  occasion?: {
    [displayValue: string]: number;
  };
  material?: {
    [displayValue: string]: number;
  };
  influencers?: {
    [displayValue: string]: number;
  };
};

export type FacetStateTypes = keyof FacetState;

export type State = {
  loading: boolean;
  titleHeadline: string;
  filterMenuOpen: boolean;
  filterState: FilterState;
  facets: FacetState;
  items: ProductCardData[];
  totalResults: number;
  totalPages: number;
  totalFilters: number;
};

type CollectionName = 'Handbags' | 'Designer Jewelry' | 'Fine Jewelry' | '';

type Action =
  | { type: 'CLEAR_SEARCH_RESULTS'; payload: boolean }
  | { type: 'SELECT_COLLECTION'; payload: CollectionName }
  // Using this loading state causes scroll jumping
  // Disabling for now, as this piece of state isn't being used
  // | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_FILTER_MENU_OPEN'; payload: boolean }
  | { type: 'SET_FILTER_STATE'; payload: FilterState }
  | { type: 'SET_FULL_STATE'; payload: State }
  | {
      type: 'SET_ITEMS';
      payload: {
        facets?: FacetState;
        items: ProductCardData[];
        totalItems: number;
        totalPages: number;
        totalFilters: number;
      };
    };

const MyWishlistTitleHeadline = 'My Wishlist';
const SearchResultsTitleHeadlinePrefix = 'Search results for';

export const initialFilterState: FilterState = {
  searchQuery: '',
  collection: {
    collectionType: [],
    types: []
  },
  subscriptionLevel: [],
  brand: [],
  color: [],
  occasion: [],
  material: [],
  influencers: [],
  inStock: false,
  newArrivals: false,
  wishlist: false,
  hitsCount: process.env.NEXT_PUBLIC_PLP_HITS_COUNT
    ? parseInt(process.env.NEXT_PUBLIC_PLP_HITS_COUNT)
    : 54,
  pageNo: 1
};

const initialState: State = {
  loading: false,
  titleHeadline: '',
  filterMenuOpen: false,
  filterState: initialFilterState,
  facets: {},
  items: [],
  totalResults: 0,
  totalPages: 0,
  totalFilters: 0
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'CLEAR_SEARCH_RESULTS': {
      return {
        ...state,
        titleHeadline: '',
        filterState: {
          ...state.filterState,
          searchQuery: ''
        }
      };
    }
    case 'SELECT_COLLECTION': {
      const newFilterState = createFilterStateCopy(state.filterState);
      if (action.payload === '') {
        newFilterState.collection.collectionType = [];
        newFilterState.pageNo = 1;
      } else {
        newFilterState.collection.collectionType[0] = action.payload;
        newFilterState.pageNo = 1;
      }

      // Remove any categories (collection.types) queries when this is clicked
      newFilterState.collection.types = [];

      return {
        ...state,
        filterState: newFilterState
      };
    }

    // Using this loading state causes scroll jumping
    // Disabling for now, as this piece of state isn't being used
    // case 'SET_LOADING': {
    //   return {
    //     ...state,
    //     loading: action.payload
    //   };
    // }

    case 'SET_FILTER_MENU_OPEN': {
      return {
        ...state,
        filterMenuOpen: action.payload
      };
    }

    case 'SET_FILTER_STATE': {
      // Determine the state of the title headline from filter state
      // We already know a copy has been sent in, so we need to alter the headline
      let titleHeadline = '';
      const filterCopy = action.payload;

      if (filterCopy.wishlist) {
        titleHeadline = MyWishlistTitleHeadline;
      }

      // A search query overrides 'my wishlist' for title headline
      if (filterCopy.searchQuery) {
        titleHeadline = SearchResultsTitleHeadlinePrefix;
      }

      return {
        ...state,
        titleHeadline,
        filterState: action.payload
      };
    }

    case 'SET_FULL_STATE': {
      return action.payload;
    }

    case 'SET_ITEMS': {
      return {
        ...state,
        items: action.payload.items,
        totalResults: action.payload.totalItems,
        totalPages: action.payload.totalPages,
        facets: action.payload.facets || {
          ...state.facets
        },
        totalFilters: action.payload.totalFilters
      };
    }

    default: {
      return state;
    }
  }
};

function ProductListApp(props: ProductListAppProps) {
  const {
    className = '',
    stateFromServer,
    currentUrlPath,
    allFacets,
    variant = PLPVariant.DEFAULT,
    showSearch = false,
    showFilters = true,
    afterPagination,
    onProductCardClick,
    heroBanner,
    baseFilterState,
    lockedFacets
  } = props;
  const isPriveeVariant = variant === PLPVariant.PRIVEE;
  const [state, dispatch] = useReducer(
    reducer,
    stateFromServer || initialState
  );
  const { actions: searchActions, value: searchValue } = useSearchContext();

  const router = useRouter();
  const user = useLoggedInUser();
  const { value: flagValues } = useFlags();
  const isPriveeOn = flagValues[FLAG_KEYS.PRIVEE];

  const { filterState } = state;

  // Base filter state that "Clear All"/Reset and the search bars revert to.
  // For campaign PLPs this is the passed-in baseFilterState (carrying the pinned
  // occasion); otherwise the Privée or default base. The hitsCount is preserved
  // from server state so logged-out Réservé visitors keep the smaller preview
  // after a reset. Memoized so the search-bar effect below keeps a stable
  // reference and doesn't re-bind its listener on every render.
  const priveeBaseFilterState = useMemo<FilterState>(
    () =>
      user
        ? priveeInitialFilterState
        : {
            ...priveeInitialFilterState,
            hitsCount:
              stateFromServer?.filterState.hitsCount ??
              priveeInitialFilterState.hitsCount
          },
    [user, stateFromServer?.filterState.hitsCount]
  );

  const resolvedBaseFilterState = useMemo<FilterState>(
    () =>
      baseFilterState ??
      (isPriveeVariant ? priveeBaseFilterState : initialFilterState),
    [baseFilterState, isPriveeVariant, priveeBaseFilterState]
  );

  /**
   * Ref used to ensure filterState update does not
   * run on initial render. This technique was borrowed from
   * https://dpericich.medium.com/how-to-bypass-useeffect-on-your-first-page-render-c31b7ba112a7
   */
  const didInitialRender = useRef(false);

  // SEARCH/UPDATE EFFECT LOOP
  useEffect(() => {
    const onFilterStateUpdate = async () => {
      // Build the query string from active filters. Omit any pinned/locked
      // occasion(s): on a campaign PLP the occasion is implied by the route and
      // re-applied server-side, so surfacing a removable-looking ?occasion= is
      // just noise. The search below still uses the full filterState, so
      // products stay scoped. User-applied filters serialize normally.
      const lockedOccasions = lockedFacets?.occasion ?? [];
      const filterStateForUrl = lockedOccasions.length
        ? {
            ...filterState,
            occasion: filterState.occasion.filter(
              (value) => !lockedOccasions.includes(value)
            )
          }
        : filterState;
      const filterQuery = getQueryParamsFromFilterState(filterStateForUrl);
      // Preserve any params we don't own (e.g. builder.preview for the CMS
      // editor, utm_* attribution) so a filter/pagination change doesn't strip
      // them.
      const preserved: string[] = [];
      if (typeof window !== 'undefined') {
        new URLSearchParams(window.location.search).forEach((value, key) => {
          if (!FILTER_QUERY_PARAM_KEYS.has(key)) {
            preserved.push(
              `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
            );
          }
        });
      }
      const queryString = [filterQuery, preserved.join('&')]
        .filter(Boolean)
        .join('&');
      const fullUrl = queryString
        ? `${currentUrlPath}?${queryString}`
        : currentUrlPath;
      router.replace(fullUrl, { scroll: false });

      // Run the search
      const result = await runSearchWithFilterState(filterState, user);
      const newState = buildStateFromSearch(result, filterState);

      // Update items and state
      dispatch({
        type: 'SET_ITEMS',
        payload: {
          facets: allFacets || newState.facets,
          items: newState.items,
          totalItems: newState.totalResults,
          totalPages: newState.totalPages,
          totalFilters: newState.totalFilters
        }
      });
    };

    // Check and see if the initial render has happened.
    // All effects run on first mount/render, so we need to keep track of this
    // so that products don't shift around unexpectedly between server
    // and client rendering
    if (didInitialRender.current === true) {
      onFilterStateUpdate();
    } else {
      didInitialRender.current = true;
    }
  }, [filterState, router, user, currentUrlPath]);

  // SEARCH QUERY EFFECT
  useEffect(() => {
    const handleSearchOnPLP = () => {
      const inputElementNodelist = document.getElementsByClassName(
        'SearchBarTextElement'
      );

      // State between mobile and desktop share the same data from useState in React
      // so it does not matter if you grab the value from the desktop or mobile <input> element
      // as the searchValue string will be the same
      const searchInputElement = inputElementNodelist[0] as HTMLInputElement;

      if (searchInputElement) {
        const searchValue = searchInputElement.value;
        const filterStateCopy = createFilterStateCopy(resolvedBaseFilterState);
        filterStateCopy.searchQuery = searchValue;

        // Do your search here
        dispatch({ type: 'SET_FILTER_STATE', payload: filterStateCopy });
      }
    };

    const headerSearchElement = document.getElementById(HEADER_SEARCH_FORM_ID);

    if (headerSearchElement) {
      headerSearchElement.addEventListener('submit', handleSearchOnPLP);
    }

    return () => {
      headerSearchElement?.removeEventListener('submit', handleSearchOnPLP);
    };
  }, [resolvedBaseFilterState]);

  useScrollLock(state.filterMenuOpen, '.FilterMenu .FilterMenu-top-wrapper');

  const handleTabSelect = (value: string) => {
    // The "Réservé" tab is a navigation link to a different page rather
    // than a filter on the current PLP.
    if (value === 'privee') {
      router.push('/collection/reserve');
      return;
    }

    let rawValue = value;

    // Accounts for 'view-all' because the value the microservice is expecting is an empty string
    if (value === 'view-all') {
      rawValue = '';
    }

    dispatch({
      type: 'SELECT_COLLECTION',
      payload: rawValue as CollectionName
    });
  };

  const handleOpenFilterMenu = () => {
    dispatch({ type: 'SET_FILTER_MENU_OPEN', payload: true });
    // Hack to get whole filter menu showing
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseFilterMenu = () => {
    dispatch({ type: 'SET_FILTER_MENU_OPEN', payload: false });
  };

  const handleResetFilters = () => {
    dispatch({
      type: 'SET_FILTER_STATE',
      payload: createFilterStateCopy(resolvedBaseFilterState)
    });
  };

  const handleFilterChange = (pendingFilterState: FilterState) => {
    dispatch({ type: 'SET_FILTER_STATE', payload: pendingFilterState });
  };

  const handleClearSearchResults = () => {
    dispatch({ type: 'CLEAR_SEARCH_RESULTS', payload: true });
    searchActions.clearSearch();
    handleResetFilters();
  };

  const handlePLPSearch = (query: string) => {
    const filterStateCopy = createFilterStateCopy(resolvedBaseFilterState);
    filterStateCopy.searchQuery = query;
    dispatch({ type: 'SET_FILTER_STATE', payload: filterStateCopy });
  };

  return (
    <div
      className={cn('ProductListApp', className, {
        privee: isPriveeVariant,
        campaign: variant === PLPVariant.CAMPAIGN
      })}
    >
      {isPriveeVariant &&
        (isPriveeMember(user) ? (
          <PriveeHeroBanner />
        ) : (
          <PriveeInterestHero thankYouRedirectPath={currentUrlPath} />
        ))}
      {heroBanner}
      <div
        id='products'
        className={cn('top-bar', {
          'top-bar-menu-open': state.filterMenuOpen
        })}
      >
        {(variant == PLPVariant.DEFAULT || variant == PLPVariant.CAMPAIGN) && (
          <TabBar
            className='ProductListApp-TabBar'
            tabs={[
              { displayName: 'VIEW ALL', value: 'view-all' },
              ...(isPriveeOn
                ? [
                    {
                      displayName: 'RÉSERVÉ',
                      value: 'privee',
                      content: (
                        <Image
                          src={PriveeWordmark}
                          width={68}
                          height={29}
                          alt='Réservé'
                        />
                      )
                    }
                  ]
                : []),
              { displayName: 'HANDBAGS', value: 'Handbags' },
              {
                displayName: 'DESIGNER JEWELRY',
                value: 'Designer Jewelry'
              },
              { displayName: 'FINE JEWELRY', value: 'Fine Jewelry' }
            ]}
            currentTabValue={
              state.filterState.collection.collectionType[0] || 'view-all'
            }
            onTabSelect={handleTabSelect}
          />
        )}
        {showSearch && (
          <PLPSearchBar
            onSearch={handlePLPSearch}
            placeholder='Search'
          />
        )}
        {showFilters && (
          <Button
            className='topbar-filter-button filter-button filter-button-desktop cursor'
            onClick={handleOpenFilterMenu}
            size='small-bold'
            variant='light'
          >
            Filters {filterCountString(state)}
          </Button>
        )}
      </div>

      {showFilters && (
        <div
          className={cn('container-FilterMenu', {
            'filter-open': state.filterMenuOpen
          })}
        >
          <div
            className={cn('overlay-FilterMenu', {
              cursor: state.filterMenuOpen
            })}
            onClick={handleCloseFilterMenu}
          />

          <FilterMenu
            className={cn('plp-FilterMenu', { open: state.filterMenuOpen })}
            state={state}
            showTierFilter={variant == PLPVariant.DEFAULT}
            showWishlistFilter={!isPriveeVariant}
            showDesignerFilter={!isPriveeVariant}
            onResetFilters={handleResetFilters}
            onCloseMenuClick={handleCloseFilterMenu}
            onFilterChange={handleFilterChange}
            lockedFacets={lockedFacets}
          />
        </div>
      )}
      <div
        className={cn('app-container', {
          'app-container-filter-menu-open': state.filterMenuOpen
        })}
      >
        <div
          className={cn('section-title', {
            'has-content': state.titleHeadline
          })}
        >
          {state.titleHeadline && (
            <div className='title-headline-wrapper'>
              <Text
                className='title-headline-text'
                italic
                variant={state.filterState.searchQuery ? 'title-m' : 'title-l'}
              >
                {state.titleHeadline}{' '}
                {state.filterState.searchQuery && (
                  <>
                    <br />
                    &lsquo;{state.filterState.searchQuery}&rsquo;
                  </>
                )}
              </Text>
              {state.titleHeadline == MyWishlistTitleHeadline && (
                <Image
                  src={PinkFlower}
                  className='title-headline-flower-logo'
                  alt='Vivrelle flower logo in pink'
                  width={24}
                  height={24}
                />
              )}

              <Button
                className='Link compact clear-results-button'
                size='small'
                variant='underline'
                onClick={handleClearSearchResults}
              >
                Clear Results
              </Button>
            </div>
          )}

          {showFilters && (
            <Button
              className='filter-button filter-button-mobile cursor'
              onClick={handleOpenFilterMenu}
              size='small-bold'
              variant='light'
            >
              Filters {filterCountString(state)}
            </Button>
          )}
        </div>

        {state.items.length === 0 && (
          <div className='plp-zero-state'>
            <div className='plp-zero-state-content'>
              <Text
                className='plp-zero-state-text'
                variant='title-m'
                italic
              >
                No results for your search
              </Text>
              <Button
                className='plp-zer-state-button'
                variant='dark'
                size='large'
                onClick={handleResetFilters}
              >
                View Full Collection
              </Button>
            </div>
          </div>
        )}

        {state.items.length !== 0 && (
          <>
            <div
              className='plp-grid-container'
              data-testid='product-grid'
            >
              {state.items.map((item) => {
                return (
                  <ProductCard
                    data={item}
                    key={item.sku}
                    onProductCardClick={onProductCardClick}
                  />
                );
              })}
            </div>
            <PaginationBar
              currentPage={filterState.pageNo}
              totalPages={state.totalPages}
              onNavigate={(page: number) => {
                const filterStateCopy = createFilterStateCopy(
                  state.filterState
                );
                filterStateCopy.pageNo = page;
                dispatch({
                  type: 'SET_FILTER_STATE',
                  payload: filterStateCopy
                });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
            {afterPagination}
          </>
        )}
      </div>
    </div>
  );
}

export { PLPVariant };
export default ProductListApp;
